/**
 * As duas conversas do assistir junto.
 *
 * Porte de `lib/features/watch_party/data/services/conversa_guardada.dart`. São
 * duas coleções com o mesmo formato — `watch_party_chats`, que fica com a
 * sessão, e `temporary_chats`, a de combinar o filme antes — e a única
 * diferença entre elas é o nome. Não há subcoleção: cada mensagem é um
 * documento solto com o campo `roomId`.
 *
 * Mora no compartilhado porque a conversa é a mesma nos dois apps: as bolhas
 * mudam de desenho entre o site e o celular, mas o que vira mensagem, a ordem e
 * o corte das 200 não mudam.
 */

import { useMemo } from 'react';
import { ligacao } from '../nucleo/ligacao';
import { usarColecao } from '../nucleo/usarArmazem';
import { gravarData, lerData } from '../nucleo/tempo';

/** A ficha crua, do jeito que o Flutter grava. */
export interface FichaDeMensagem {
  roomId?: string;
  sender?: string;
  senderUid?: string;
  text?: string;
  timestamp?: string;
}

export interface Mensagem {
  id: string;
  /** O nome de quem escreveu, como veio gravado. */
  quem: string;
  /** O slug de quem escreveu; é por ele que a bolha decide o lado. */
  slug: string;
  texto: string;
  quando: Date;
}

/**
 * Quantas mensagens a tela guarda.
 *
 * As mais antigas continuam gravadas — o Flutter corta na mesma conta, e do
 * mesmo lado: as 200 *últimas*.
 */
const QUANTAS = 200;

/** A conversa de uma sala, da mais antiga para a mais nova, ao vivo. */
export function usarConversa(colecao: string, sala: string): Mensagem[] {
  const tudo = usarColecao<FichaDeMensagem>(colecao);
  return useMemo(() => {
    if (!sala) return [];
    const minhas = tudo
      .filter((d) => d.dados.roomId === sala)
      .map((d) => ({
        id: d.id,
        quem: d.dados.sender ?? '',
        slug: d.dados.senderUid ?? '',
        texto: d.dados.text ?? '',
        quando: lerData(d.dados.timestamp) ?? new Date(0),
      }))
      .sort((a, b) => a.quando.getTime() - b.quando.getTime());
    return minhas.length <= QUANTAS ? minhas : minhas.slice(minhas.length - QUANTAS);
  }, [tudo, sala]);
}

/** Escreve uma mensagem na sala. */
export async function enviarMensagem(
  colecao: string,
  sala: string,
  texto: string,
  quem: string,
  slug: string,
): Promise<void> {
  const { armazem } = ligacao();
  const limpo = texto.trim();
  if (!sala || !limpo) return;
  await armazem.adicionar(colecao, {
    roomId: sala,
    sender: quem,
    senderUid: slug,
    text: limpo,
    /* A hora é a do aparelho, como no Flutter: o que a conversa precisa é da
       ordem, e para isso o relógio local basta. */
    timestamp: gravarData(),
  });
}

/** Apaga a conversa inteira de uma sala. */
export async function limparConversa(colecao: string, sala: string): Promise<void> {
  if (!sala) return;
  const { armazem } = ligacao();
  const antigas = armazem.colecao<FichaDeMensagem>(colecao).filter((d) => d.dados.roomId === sala);
  await Promise.all(antigas.map((d) => armazem.apagar(colecao, d.id)));
}
